import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title2,
  Body1,
  Spinner,
  makeStyles,
  tokens,
  Card,
  Button,
} from '@fluentui/react-components';
import {
  PeopleRegular,
  CalendarRegular,
  PersonRegular,
  ChatRegular,
  CheckmarkRegular,
  DismissCircleRegular,
  ClockRegular,
  ShieldRegular,
  ListRegular,
  StarRegular,
  SparkleRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../hooks/useAuth';
import { getSharePointService } from '../services/sharePointService';
import { Volunteer, VolunteerStatus } from '../types/sharepoint';


const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '32px',
    padding: '32px 24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    background: `linear-gradient(135deg, ${tokens.colorNeutralBackground1} 0%, ${tokens.colorNeutralBackground2} 100%)`,
    borderRadius: '12px',
    boxShadow: tokens.shadow2,
  },
  headerTop: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.2',
    letterSpacing: '-0.02em',
    marginBottom: '8px',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
    fontWeight: tokens.fontWeightRegular,
    lineHeight: '1.4',
    margin: 0,
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '32px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '12px',
      marginBottom: '24px',
    },
  },
  statCard: {
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow2,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'all 0.2s',
    ':hover': {
      boxShadow: tokens.shadow4,
      transform: 'translateY(-2px)',
    },
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    flexShrink: 0,
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  statValue: {
    fontSize: '24px',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.2',
  },
  statLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightMedium,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  quickActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px',
    marginBottom: '32px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gap: '12px',
      marginBottom: '24px',
    },
  },
  actionCard: {
    padding: '24px',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow2,
    transition: 'all 0.2s',
    cursor: 'pointer',
    ':hover': {
      boxShadow: tokens.shadow4,
      transform: 'translateY(-2px)',
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  actionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  actionIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },
  actionTitle: {
    fontSize: '16px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  actionDescription: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '1.4',
    margin: 0,
  },
  recentActivity: {
    marginTop: '32px',
  },
  activityTitle: {
    fontSize: '18px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '16px',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  activityItem: {
    padding: '16px',
    borderRadius: '8px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  activityIcon: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  activityTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    margin: 0,
  },
});

const Home: React.FC = () => {
  const styles = useStyles();
  const { user } = useAuth();
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const navigate = useNavigate();

  const loadVolunteers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSharePointService().getVolunteers();
      setVolunteers(data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading volunteers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVolunteers();
  }, [loadVolunteers]);

  const getWelcomeMessage = () => {
    if (!user) return 'Welcome to TruCare Portal';
    
    // Use the user's first name if available, otherwise use their full name
    const userName = user.name || user.email || 'User';
    const firstName = userName.split(' ')[0]; // Get first name
    
    return `Welcome, ${firstName}`;
  };

  const getVolunteerStats = () => {
    const total = volunteers.length;
    const active = volunteers.filter(v => v.volunteerStatus === VolunteerStatus.CONTACT_COMPLETE).length;
    const inactive = volunteers.filter(v => v.volunteerStatus === VolunteerStatus.INACTIVE).length;
    const pending = volunteers.filter(v => v.volunteerStatus === VolunteerStatus.NEW_NEEDS_CONTACT).length;

    return { total, active, inactive, pending };
  };

  const getRoleSpecificStats = () => {
    if (!user) return [];

    switch (user.role) {
      case 'admin':
        return [
          { label: 'Total Volunteers', value: getVolunteerStats().total, icon: PeopleRegular, color: tokens.colorPaletteBlueBackground2 },
          { label: 'Active Volunteers', value: getVolunteerStats().active, icon: CheckmarkRegular, color: tokens.colorPaletteGreenBackground2 },
          { label: 'Pending Contact', value: getVolunteerStats().pending, icon: ClockRegular, color: tokens.colorPaletteYellowBackground2 },
          { label: 'Inactive', value: getVolunteerStats().inactive, icon: DismissCircleRegular, color: tokens.colorPaletteRedBackground2 },
        ];
      case 'scheduler':
        return [
          { label: 'Available Volunteers', value: volunteers.filter(v => v.augustAvailability === 'Available').length, icon: PeopleRegular, color: tokens.colorPaletteGreenBackground2 },
          { label: 'Assigned Roles', value: volunteers.filter(v => v.primaryArea).length, icon: ListRegular, color: tokens.colorPaletteBlueBackground2 },
          { label: 'Pending Assignments', value: volunteers.filter(v => !v.primaryArea && v.volunteerStatus === VolunteerStatus.CONTACT_COMPLETE).length, icon: ClockRegular, color: tokens.colorPaletteYellowBackground2 },
          { label: 'Total Volunteers', value: getVolunteerStats().total, icon: PeopleRegular, color: tokens.colorPalettePurpleBackground2 },
        ];
      case 'onboarding':
        return [
          { label: 'New Volunteers', value: volunteers.filter(v => v.volunteerStatus === VolunteerStatus.NEW_NEEDS_CONTACT).length, icon: PersonRegular, color: tokens.colorPaletteBlueBackground2 },
          { label: 'In Compliance', value: volunteers.filter(v => v.assignedManager === 'Compliance').length, icon: ShieldRegular, color: tokens.colorPaletteGreenBackground2 },
          { label: 'Pending Contact', value: volunteers.filter(v => v.volunteerStatus === VolunteerStatus.CONTACTED_NO_RESPONSE).length, icon: ClockRegular, color: tokens.colorPaletteYellowBackground2 },
          { label: 'Total Volunteers', value: getVolunteerStats().total, icon: PeopleRegular, color: tokens.colorPalettePurpleBackground2 },
        ];
      default:
        return [
          { label: 'Total Volunteers', value: getVolunteerStats().total, icon: PeopleRegular, color: tokens.colorPaletteBlueBackground2 },
          { label: 'Active', value: getVolunteerStats().active, icon: CheckmarkRegular, color: tokens.colorPaletteGreenBackground2 },
          { label: 'Inactive', value: getVolunteerStats().inactive, icon: DismissCircleRegular, color: tokens.colorPaletteRedBackground2 },
          { label: 'Pending', value: getVolunteerStats().pending, icon: ClockRegular, color: tokens.colorPaletteYellowBackground2 },
        ];
    }
  };

  const getQuickActions = () => {
    if (!user) return [];

    switch (user.role) {
      case 'admin':
        return [
          {
            title: 'Volunteer Roster',
            description: 'View and manage all volunteers',
            icon: PeopleRegular,
            color: tokens.colorPaletteBlueBackground2,
            path: '/volunteers',
          },
          {
            title: 'Scheduling Portal',
            description: 'Schedule and assign volunteers',
            icon: CalendarRegular,
            color: tokens.colorPaletteGreenBackground2,
            path: '/volunteer-list',
          },
        ];
      case 'executive':
        return [
          {
            title: 'Volunteer Roster',
            description: 'View and manage all volunteers',
            icon: PeopleRegular,
            color: tokens.colorPaletteBlueBackground2,
            path: '/volunteers',
          },
          {
            title: 'Scheduling Portal',
            description: 'Schedule and assign volunteers',
            icon: CalendarRegular,
            color: tokens.colorPaletteGreenBackground2,
            path: '/volunteer-list',
          },
        ];
      case 'manager':
        return [
          {
            title: 'Volunteer Roster',
            description: 'View and manage all volunteers',
            icon: PeopleRegular,
            color: tokens.colorPaletteBlueBackground2,
            path: '/volunteers',
          },
          {
            title: 'Scheduling Portal',
            description: 'Schedule and assign volunteers',
            icon: CalendarRegular,
            color: tokens.colorPaletteGreenBackground2,
            path: '/volunteer-list',
          },
        ];
      case 'scheduler':
        return [
          {
            title: 'Scheduling Portal',
            description: 'Schedule and assign volunteers to roles',
            icon: CalendarRegular,
            color: tokens.colorPaletteGreenBackground2,
            path: '/volunteer-list',
          },
          {
            title: 'Volunteer Roster',
            description: 'View volunteer profiles and availability',
            icon: PeopleRegular,
            color: tokens.colorPaletteBlueBackground2,
            path: '/volunteers',
          },
          {
            title: 'Schedule Management',
            description: 'Create and manage clinic schedules',
            icon: ListRegular,
            color: tokens.colorPalettePurpleBackground2,
            path: '/app/schedule',
          },
        ];
      case 'onboarding':
        return [
          {
            title: 'Volunteer Roster',
            description: 'Review and manage volunteer profiles',
            icon: PeopleRegular,
            color: tokens.colorPaletteBlueBackground2,
            path: '/volunteers',
          },
          {
            title: 'Compliance Tracking',
            description: 'Monitor volunteer compliance status',
            icon: ShieldRegular,
            color: tokens.colorPaletteGreenBackground2,
            path: '/volunteers',
          },
          {
            title: 'Contact Management',
            description: 'Manage volunteer communications',
            icon: ChatRegular,
            color: tokens.colorPalettePurpleBackground2,
            path: '/app/volunteers',
          },
        ];
      default:
        return [
          {
            title: 'Volunteer Roster',
            description: 'View volunteer information',
            icon: PeopleRegular,
            color: tokens.colorPaletteBlueBackground2,
            path: '/app/volunteers',
          },
        ];
    }
  };

  const handleActionClick = (path: string) => {
    navigate(path);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spinner size="large" label="Loading dashboard..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleContainer}>
            <Title2 className={styles.title}>{getWelcomeMessage()}</Title2>
            <Body1 className={styles.subtitle}>
              Your central hub for managing TruCare volunteer operations
            </Body1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ClockRegular style={{ fontSize: '14px', color: tokens.colorNeutralForeground3 }} />
            <Body1 style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Body1>
          </div>
        </div>
      </div>

      {/* What's New Banner */}
      <Card style={{ 
        marginBottom: '24px', 
        background: `linear-gradient(135deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorBrandBackground2} 100%)`,
        border: 'none',
        padding: '20px 24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <StarRegular style={{ fontSize: '24px', color: 'white' }} />
            <div>
              <Title2 style={{ color: 'white', margin: 0, fontSize: '20px' }}>
                What's New in TruCare
              </Title2>
              <Body1 style={{ color: 'rgba(255, 255, 255, 0.9)', marginTop: '4px', fontSize: '14px' }}>
                New themes, improved org chart, and enhanced volunteer management
              </Body1>
            </div>
          </div>
          <Button
            appearance="secondary"
            icon={<SparkleRegular />}
            onClick={() => {
              // Trigger the updates modal - we'll need to access it from Layout
              window.dispatchEvent(new CustomEvent('openUpdatesModal'));
            }}
            style={{ 
              backgroundColor: 'white', 
              color: tokens.colorBrandBackground,
              fontWeight: tokens.fontWeightSemibold,
            }}
          >
            View Details
          </Button>
        </div>
      </Card>

      {/* Role-Specific Statistics */}
      <div className={styles.stats}>
        {getRoleSpecificStats().map((stat, index) => (
          <div key={index} className={styles.statCard}>
            <div className={styles.statIcon} style={{ backgroundColor: stat.color }}>
              <stat.icon />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stat.value}</div>
              <div className={styles.statLabel}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        {getQuickActions().map((action, index) => (
          <div 
            key={index} 
            className={styles.actionCard}
            onClick={() => handleActionClick(action.path)}
          >
            <div className={styles.actionHeader}>
              <div className={styles.actionIcon} style={{ backgroundColor: action.color }}>
                <action.icon />
              </div>
              <h3 className={styles.actionTitle}>{action.title}</h3>
            </div>
            <p className={styles.actionDescription}>{action.description}</p>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className={styles.recentActivity}>
        <h3 className={styles.activityTitle}>Recent Activity</h3>
        <div className={styles.activityList}>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon} style={{ backgroundColor: tokens.colorPaletteGreenBackground2 }}>
              <CheckmarkRegular />
            </div>
            <div className={styles.activityContent}>
              <p className={styles.activityText}>Dashboard loaded successfully</p>
              <p className={styles.activityTime}>{lastRefresh.toLocaleString()}</p>
            </div>
          </div>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon} style={{ backgroundColor: tokens.colorPaletteBlueBackground2 }}>
              <PeopleRegular />
            </div>
            <div className={styles.activityContent}>
              <p className={styles.activityText}>{volunteers.length} volunteers loaded</p>
              <p className={styles.activityTime}>{lastRefresh.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default Home; 